import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, tap } from 'rxjs';

import { CollegePortalService } from '../services/college-portal.service';
import { CollegeProfile } from '../models/college-portal.models';
import { COURSES_LIST } from '@edtech-ui/core/constants/education-master-data';
import { COLLEGE_BRANCHES_LIST, PLACEMENT_INDUSTRY_TYPES } from '@edtech-ui/core/constants/college-constants/college-options.constants';
import { phoneValidator } from '../../hiring-workspace/recruiter-auth/recruiter-auth.component';

@Component({
	selector: 'app-college-profile',
	standalone: true,
	imports: [CommonModule, RouterModule, ReactiveFormsModule, MatProgressSpinnerModule],
	templateUrl: './college-profile.component.html',
	styleUrls: ['./college-profile.component.scss']
})
export class CollegeProfileComponent implements OnInit {
	@Output() profileSaved = new EventEmitter<CollegeProfile>();

	isLoading = false;
	isEditMode = false;
	collegeProfile!: CollegeProfile;
	collegeProfileForm!: FormGroup;
	selectedLogoFile: File | null = null;

	// Streams / courses / placement industries — same hand-rolled chip
	// multi-select pattern used on the registration form (no reusable
	// multi-select component exists in this app).
	streamOptions = COURSES_LIST;
	streams: string[] = [];
	filteredStreams: string[] = [];
	showStreamsDropdown = false;

	branchOptions = COLLEGE_BRANCHES_LIST;
	coursesOffered: string[] = [];
	filteredBranches: string[] = [];
	showBranchesDropdown = false;

	industryOptions = PLACEMENT_INDUSTRY_TYPES;
	placementIndustries: string[] = [];
	filteredIndustries: string[] = [];
	showIndustriesDropdown = false;

	constructor(
		private _location: Location,
		private _collegePortalService: CollegePortalService,
		private toastr: ToastrService
	) {
		this.collegeProfileForm = new FormGroup({
			college_name: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(255)]),
			college_url: new FormControl('', [Validators.pattern('^(https?:\\/\\/)?(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)$')]),
			university_name: new FormControl(''),
			ranking: new FormControl(''),
			city: new FormControl(''),
			state: new FormControl(''),
			country: new FormControl(''),
			logo_url: new FormControl(''),
			point_of_contact_name: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(255)]),
			point_of_contact_email: new FormControl('', [Validators.required, Validators.email]),
			phone_number: new FormControl('', [Validators.required, phoneValidator()])
		});
	}

	ngOnInit(): void {
		this.getCollegeProfile();
	}

	getLogoUrl(url: string | null | undefined): string {
		// College logos are always full S3 URLs (or a data: URL while previewing
		// a freshly selected file) — no relative-path base prefixing needed.
		return url || '';
	}

	getCollegeProfile(): void {
		this.isLoading = true;
		this._collegePortalService.getCollegeProfile().pipe(
			tap(response => {
				if (response.Success) {
					this.collegeProfile = response.Data;
					this.patchCollegeProfile(response.Data);
				} else {
					this.toastr.error(response.Message);
				}
			}),
			finalize(() => { this.isLoading = false; }),
			catchError(() => {
				this.toastr.error('Something went wrong. Please try again.');
				return of(null);
			})
		).subscribe();
	}

	patchCollegeProfile(data: CollegeProfile): void {
		this.streams = [...(data.streams || [])];
		this.coursesOffered = [...(data.courses_offered || [])];
		this.placementIndustries = [...(data.placement_industries || [])];

		this.collegeProfileForm.patchValue({
			college_name: data.college_name,
			college_url: data.college_url,
			university_name: data.university_name,
			ranking: data.ranking,
			city: data.city,
			state: data.state,
			country: data.country,
			logo_url: data.college_logo_url,
			point_of_contact_name: data.point_of_contact_name,
			point_of_contact_email: data.point_of_contact_email,
			phone_number: data.phone_number
		});
	}

	goBack(): void {
		this._location.back();
	}

	toggleEditMode(): void {
		this.isEditMode = true;
	}

	cancelEdit(): void {
		this.patchCollegeProfile(this.collegeProfile);
		this.selectedLogoFile = null;
		this.isEditMode = false;
	}

	saveProfile(): void {
		if (this.collegeProfileForm.invalid) {
			this.collegeProfileForm.markAllAsTouched();
			return;
		}
		if (this.streams.length === 0) {
			this.toastr.error('Please add at least one stream.');
			return;
		}
		if (this.placementIndustries.length === 0) {
			this.toastr.error('Please add at least one placement industry.');
			return;
		}

		const formValue = this.collegeProfileForm.value;
		const formData = new FormData();
		formData.append('college_name', (formValue.college_name || '').trim());
		formData.append('college_url', (formValue.college_url || '').trim());
		formData.append('university_name', (formValue.university_name || '').trim());
		formData.append('ranking', (formValue.ranking || '').trim());
		formData.append('city', (formValue.city || '').trim());
		formData.append('state', (formValue.state || '').trim());
		formData.append('point_of_contact_name', (formValue.point_of_contact_name || '').trim());
		formData.append('point_of_contact_email', (formValue.point_of_contact_email || '').trim());
		formData.append('phone_number', (formValue.phone_number || '').trim());
		this.streams.forEach(s => formData.append('streams', s));
		this.coursesOffered.forEach(c => formData.append('courses_offered', c));
		this.placementIndustries.forEach(i => formData.append('placement_industries', i));
		if (this.selectedLogoFile) {
			formData.append('logo', this.selectedLogoFile);
		}

		this.isLoading = true;
		this._collegePortalService.updateCollegeProfile(formData).pipe(
			tap(response => {
				if (response.Success) {
					this.toastr.success('College profile updated successfully');
					this.collegeProfile = response.Data;
					this.patchCollegeProfile(response.Data);
					this.selectedLogoFile = null;
					this.isEditMode = false;
					this.profileSaved.emit(response.Data);
				} else {
					this.toastr.error(response.Message);
				}
			}),
			finalize(() => { this.isLoading = false; }),
			catchError((err) => {
				this.toastr.error(err?.error?.Message || 'Failed to update college profile');
				return of(null);
			})
		).subscribe();
	}

	// ─────────────────────────────────────────────
	// Logo upload — same pattern as registration
	// ─────────────────────────────────────────────

	triggerFileInput(fileInput: HTMLInputElement): void {
		fileInput.click();
	}

	onLogoSelected(event: any): void {
		const file = event.target.files[0];
		if (!file) return;
		const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
		if (!allowedTypes.includes(file.type)) {
			this.toastr.error('Only PNG, JPG, SVG and WebP files are allowed');
			return;
		}
		if (file.size > 2 * 1024 * 1024) {
			this.toastr.error('File size should be less than 2MB');
			return;
		}
		this.selectedLogoFile = file;
		const reader = new FileReader();
		reader.onload = (e: any) => {
			this.collegeProfileForm.patchValue({ logo_url: e.target.result });
		};
		reader.readAsDataURL(file);
	}

	removeLogo(): void {
		this.selectedLogoFile = null;
		this.collegeProfileForm.patchValue({ logo_url: null });
	}

	// ─────────────────────────────────────────────
	// Multi-select chip inputs (streams / courses / industries)
	// ─────────────────────────────────────────────

	private filterOptions(options: string[], query: string): string[] {
		const q = query.trim().toLowerCase();
		if (!q) return [];
		return options.filter(o => o.toLowerCase().includes(q));
	}

	onStreamInput(event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		this.filteredStreams = this.filterOptions(this.streamOptions, value);
		this.showStreamsDropdown = this.filteredStreams.length > 0;
	}

	addStream(value: string): void {
		const match = this.streamOptions.find(o => o.toLowerCase() === value.trim().toLowerCase());
		if (match && !this.streams.includes(match)) this.streams.push(match);
		this.showStreamsDropdown = false;
		this.filteredStreams = [];
	}

	removeStream(index: number): void {
		this.streams.splice(index, 1);
	}

	onBranchInput(event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		this.filteredBranches = this.filterOptions(this.branchOptions, value);
		this.showBranchesDropdown = this.filteredBranches.length > 0;
	}

	addBranch(value: string): void {
		const match = this.branchOptions.find(o => o.toLowerCase() === value.trim().toLowerCase());
		if (match && !this.coursesOffered.includes(match)) this.coursesOffered.push(match);
		this.showBranchesDropdown = false;
		this.filteredBranches = [];
	}

	removeBranch(index: number): void {
		this.coursesOffered.splice(index, 1);
	}

	onIndustryInput(event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		this.filteredIndustries = this.filterOptions(this.industryOptions, value);
		this.showIndustriesDropdown = this.filteredIndustries.length > 0;
	}

	addIndustry(value: string): void {
		const match = this.industryOptions.find(o => o.toLowerCase() === value.trim().toLowerCase());
		if (match && !this.placementIndustries.includes(match)) this.placementIndustries.push(match);
		this.showIndustriesDropdown = false;
		this.filteredIndustries = [];
	}

	removeIndustry(index: number): void {
		this.placementIndustries.splice(index, 1);
	}
}
